// MenuItems.ts
import React from "react"
import type { MenuProps } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import {
  faCircleQuestion,
  faCode,
  faDice,
  faFileExport,
  faFileWord,
  faFont,
  faHeading,
  faLayerGroup,
  faList,
  faNoteSticky,
  faPlay,
  faRightLeft,
  faTableList,
  faUpload,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons"

// Define interfaces for menu item structure
interface BaseMenuItem {
  label: string
  key: string
}

interface SubMenuItem extends BaseMenuItem {
  children: MenuItem[]
}

interface QuestionTypeChild {
  label: string
  key: string
}

interface QuestionType {
  label: string
  key: string
  children: QuestionTypeChild[]
}

// Union type for menu items
type MenuItem = BaseMenuItem | SubMenuItem

/**
 * Generates a list of question submenus.
 *
 * @param subItems - List of sub-items.
 * @returns Generated question submenus.
 */
const generateQuestionSubmenus = (subItems: QuestionType[]): SubMenuItem[] => {
  return subItems.map(
    (item: QuestionType): SubMenuItem => ({
      label: item.label,
      key: item.key,
      children: item.children.map(
        (child: QuestionTypeChild): BaseMenuItem => ({
          label: child.label,
          key: `${item.key}_${child.key}`,
        })
      ),
    })
  )
}

const questionTypes: QuestionType[] = [
  {
    label: "Single Select",
    key: "single_select",
    children: [
      { label: "Basic", key: "basic" },
      { label: "User Predefined List", key: "user_predefined_list" },
      {
        label: "Extended With Predefined List",
        key: "extended_with_predefined_list",
      },
    ],
  },
  {
    label: "Multiple Select",
    key: "multiple_select",
    children: [
      { label: "Basic", key: "basic" },
      { label: "User Predefined List", key: "user_predefined_list" },
      {
        label: "Extended With Predefined List",
        key: "extended_with_predefined_list",
      },
    ],
  },
  {
    label: "Grid Single Select",
    key: "grid_single_select",
    children: [
      { label: "Basic", key: "basic" },
      { label: "User Predefined List", key: "user_predefined_list" },
      {
        label: "Extended With Predefined List",
        key: "extended_with_predefined_list",
      },
    ],
  },
  {
    label: "Grid Multiple Select",
    key: "grid_multiple_select",
    children: [
      { label: "Basic", key: "basic" },
      { label: "User Predefined List", key: "user_predefined_list" },
      {
        label: "Extended With Predefined List",
        key: "extended_with_predefined_list",
      },
    ],
  },
]

/** A menu row's icon. */
const icon = (fa: IconDefinition) =>
  React.createElement(FontAwesomeIcon, { icon: fa, fixedWidth: true })

const divider = (key: string) => ({ type: "divider" as const, key })

type AntMenuItem = NonNullable<MenuProps["items"]>[number]

/**
 * Gives every submenu, at any depth, the shared dropdown look
 * (styles/buttonTokens.css, `.dq-menu-popup`), so only these menus change.
 */
const withPopupClass = (list: AntMenuItem[]): AntMenuItem[] =>
  list.map((item) => {
    if (item && "children" in item && Array.isArray(item.children)) {
      return {
        ...item,
        popupClassName: "dq-menu-popup",
        children: withPopupClass(item.children as AntMenuItem[]),
      } as AntMenuItem
    }
    return item
  })

/**
 * Menu items configuration for the application. The keys are what
 * QuestionnaireMenuBar's click handler and MenuActions act on: regroup and
 * relabel freely, never rename a key.
 */
const items: MenuProps["items"] = withPopupClass([
  {
    label: "File",
    key: "file",
    children: [
      {
        label: "Convert Word questionnaire…",
        key: "convert_questionnaire_ai",
        icon: icon(faUpload),
      },
      divider("file-div-1"),
      {
        label: "Export",
        key: "export",
        icon: icon(faFileExport),
        children: [
          { label: "Questionnaire (Word)", key: "export_questionnaire", icon: icon(faFileWord) },
          { label: "Decipher XML", key: "export_to_decipher_xml", icon: icon(faCode) },
          { label: "Confirmit XML", key: "export_to_confirmit_xml", icon: icon(faCode) },
          { label: "Data map", key: "export_data_map", icon: icon(faTableList) },
        ],
      },
      {
        label: "Fill export map from source…",
        key: "fill_export_map_source",
        icon: icon(faRightLeft),
      },
      divider("file-div-2"),
      // Opens the Dummy data method of the Data Import tab.
      { label: "Generate dummy data…", key: "generate_dummy_data", icon: icon(faDice) },
      // Opens the Test survey drawer: a click-through of the saved questionnaire.
      { label: "Test survey…", key: "test_survey", icon: icon(faPlay) },
    ],
  },
  {
    label: "Insert",
    key: "insert",
    children: [
      { label: "Note", key: "note", icon: icon(faNoteSticky) },
      { label: "Section", key: "section", icon: icon(faHeading) },
      { label: "Text", key: "text", icon: icon(faFont) },
      divider("insert-div-1"),
      {
        label: "List",
        key: "list",
        icon: icon(faList),
        children: [
          { label: "Simple List", key: "simple_list" },
          {
            label: "List With Various Representations",
            key: "list_with_various_representations",
          },
          { label: "Exception List", key: "exception_list" },
        ],
      },
      {
        label: "Question",
        key: "question",
        icon: icon(faCircleQuestion),
        children: [
          {
            label: "Fill-in",
            key: "fill_in",
            children: [
              { label: "Number", key: "fill_in_number" },
              { label: "Text", key: "fill_in_text" },
            ],
          },
          ...generateQuestionSubmenus(questionTypes).map(item => ({ ...item, type: undefined })),
          { label: "Numerical Grid", key: "numerical_grid" },
        ],
      },
      divider("insert-div-2"),
      {
        label: "Special",
        key: "special",
        icon: icon(faWandMagicSparkles),
        children: [
          { label: "Hidden Question", key: "hidden_question" },
          { label: "Data", key: "data" },
          { label: "Recode", key: "recode" },
          {
            label: "Qnr Changes in a Variant",
            key: "qnr_changes_in_a_variant",
            children: [
              { label: "Hide a Question", key: "hide_a_question" },
              { label: "Extend a List", key: "extend_a_list" },
              { label: "Trim a List", key: "trim_a_list" },
              { label: "Reposition Questions", key: "reposition_questions" },
            ],
          },
        ],
      },
      { label: "Template", key: "template", icon: icon(faLayerGroup) },
    ],
  },
] as AntMenuItem[])

export default items
